import React, { useRef, useEffect, useState } from 'react';
import { ComposedChart, Bar, XAxis, YAxis, Line, CartesianGrid, Legend, ResponsiveContainer, LabelList } from 'recharts';
import styles from "./barAndLineComboChart.module.css";

const BAR_WIDTH = 100;
const WINDOW_SIZE = 25; // Number of bars to show at once

const BarAndLineChartSupplierInsights = ({ data }) => {
    const [startIndex, setStartIndex] = useState(0);
    const chartContainerRef = useRef(null);

    // Only show a window of data
    const visibleData = data?.slice(startIndex, startIndex + WINDOW_SIZE) || [];

    // Handle horizontal scroll to load more data
    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const newStart = Math.floor(container.scrollLeft / BAR_WIDTH);
            if (newStart !== startIndex && newStart >= 0 && newStart <= data.length - WINDOW_SIZE) {
                setStartIndex(newStart);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [data, startIndex]);

    const transformedData = visibleData?.map(item => ({
        vendorOtif: item["Vendor OTIF"] || 0,
        qcCompliance: item["QC compliance"] || 0,
        supplierPerformanceIndex: item["Supplier performance index"] || 0,
        categories: item["categories"] || 0
    }));

    const CustomLegend = (props) => {
        const { payload } = props;
        return (
            <div style={{ display: 'flex', justifyContent: 'left', padding: '10px' }}>
                {payload.map((entry, index) => {
                    const { value, color } = entry;
                    return (
                        <span
                            key={`item-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '0 10px',
                            }}
                        >
                            <span
                                style={{
                                    backgroundColor: color,
                                    width: '6px',
                                    height: '12px',
                                    display: 'inline-block',
                                    borderRadius: '2px',
                                }}
                            />
                            <span style={{ color: '#4D4D4F' }}>{value}</span>
                        </span>
                    );
                })}
            </div>
        );
    };

    const legendStyle = {
        fontSize: '10px',
        paddingTop: '15px',
        color: '#4D4D4F',
    };

    const CustomizedAxisTick = (props) => {
        const { x, y, payload } = props;
        const values = payload.value.split(' ');

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} textAnchor="middle" fill="#2B2B2B" fontWeight={500} fontSize={10} >
                    <tspan x="0" dy="1">{values[0]}</tspan>
                    <tspan x="0" dy="10">{values[1]}</tspan>
                </text>
            </g>
        );
    };

    const barMax = Math.max(
        ...transformedData.map(d => Math.max(d.vendorOtif || 0, d.qcCompliance || 0)),
        0
    );

    // Add a gap of 10 between bars and line
    const leftMax = Math.ceil(barMax / 10) * 30;
    const rightMin = leftMax + 10;
    const isScrollable = data.length > WINDOW_SIZE;

    return (
        <div
            className={styles.scrollbarContainer}
            ref={chartContainerRef}
            style={{ overflowX: isScrollable ? "auto" : "hidden", width: '100%', position: 'relative' }}
        >
            {/* Phantom div for scroll area */}
            <div
                style={{
                    // width: `${data.length * BAR_WIDTH}px`,
                    width: isScrollable
                        ? `${data.length * BAR_WIDTH}px`
                        : "100%",
                    height: 270,
                    position: 'relative'
                }}
            >
                {/* Chart absolutely positioned and offset by scroll */}
                <div
                    style={{
                        position: 'absolute',
                        left: `${startIndex * BAR_WIDTH}px`,
                        top: 0,
                        // width: `${WINDOW_SIZE * BAR_WIDTH}px`,
                        width: isScrollable
                            ? `${WINDOW_SIZE * BAR_WIDTH}px`
                            : "100%", // Fill space without scroll
                        height: 270,
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={transformedData}
                            margin={{
                                top: 20,
                                right: 0,
                                bottom: 10,
                                left: 0,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />
                            <XAxis
                                dataKey="categories"
                                tickLine={false}
                                axisLine={false}
                                className={styles.BarLabel}
                                tick={<CustomizedAxisTick />}
                                padding={{ left: 0, right: 0 }}
                                interval={0}
                            />
                            <YAxis
                                yAxisId="left"
                                orientation="left"
                                domain={[0, leftMax]}
                                hide={true}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={[rightMin, 100]}
                                hide={true}
                            />
                            <Legend verticalAlign="bottom" align="left" wrapperStyle={legendStyle} content={<CustomLegend />} />
                            <Bar
                                yAxisId="left"
                                radius={[4, 4, 0, 0]}
                                dataKey="vendorOtif"
                                name="Last four weeks OTIF"
                                fill="#005EEF"
                                barSize={15}
                            >
                                <LabelList
                                    dataKey="vendorOtif"
                                    position="top"
                                    style={{ fill: '#2B2B2B', fontSize: 10, fontWeight: "500" }}
                                    formatter={(value) => `${value}%`}
                                />
                            </Bar>
                            <Bar
                                yAxisId="left"
                                dataKey="qcCompliance"
                                name="QC compliance"
                                fill="#00B190"
                                barSize={15}
                                radius={[4, 4, 0, 0]}
                            >
                                <LabelList
                                    dataKey="qcCompliance"
                                    position="top"
                                    style={{ fill: '#2B2B2B', fontSize: 10, fontWeight: "500" }}
                                    formatter={(value) => `${value}%`}
                                />
                            </Bar>
                            <Line
                                yAxisId="right"
                                type="linear"
                                dataKey="supplierPerformanceIndex"
                                name="Supplier performance index"
                                stroke="#FF9E00"
                                fill="#FF9E00"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            >
                                <LabelList
                                    dataKey="supplierPerformanceIndex"
                                    position="top"
                                    style={{ fill: '#2B2B2B', fontSize: 10, fontWeight: "400" }}
                                    formatter={(value) => `${value}%`}
                                />
                            </Line>
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default BarAndLineChartSupplierInsights;